/**
 * Types for the Customization Template admin API.
 *
 * Plan: 117-personalization-admin-ui, Epic 01
 */

// -- List page types --

export interface TemplateListItem {
  name: string;
  template_name: string;
  status: "Draft" | "Active";
  field_count: number;
  rule_count: number;
  modified: string;
}

export interface TemplateListResponse {
  templates: TemplateListItem[];
  total: number;
}

// -- Detail page types --

export interface TemplateField {
  name?: string; // Row name (exists on saved fields)
  label: string;
  field_type: FieldType;
  required: 0 | 1;
  options: string;
  /** JSON map of option value -> image URL (Single/Multi-Select artwork
   * thumbnails). Empty => text options. TKT-2026-00126. */
  option_images?: string;
  addon_item: string;
  addon_price: number;
  max_characters: number;
  allowed_file_types: string;
  max_file_size: number;
  sort_order: number;
}

export interface TemplateRule {
  name?: string;
  when_field: string;
  operator: string;
  has_value: string;
  then_action: string;
  target_field: string;
  target_value: string;
  rule_group: string;
  priority: number;
  summary: string;
}

export interface TemplateDetail {
  name: string;
  template_name: string;
  description: string;
  status: "Draft" | "Active";
  company: string;
  fields: TemplateField[];
  rules: TemplateRule[];
}

// -- Mutation types --

export interface SaveTemplateInput {
  name?: string;
  template_name: string;
  description: string;
  status: "Draft" | "Active";
  fields: TemplateField[];
  rules: TemplateRule[];
}

export interface SaveTemplateResponse {
  name: string;
  template_name: string;
  status: string;
}

export interface DeleteTemplateResponse {
  success: boolean;
  linked_products_cleared: number;
}

export interface AddFieldInput {
  template_name: string;
  label: string;
  field_type: FieldType;
  required?: 0 | 1;
  options?: string;
  addon_item?: string;
  max_characters?: number;
  allowed_file_types?: string;
  max_file_size?: number;
}

export interface AddFieldResponse {
  name: string;
  idx: number;
  label: string;
  field_type: string;
  sort_order: number;
}

// -- Activation validation --

export interface ActivationError {
  field_label: string;
  message: string;
}

export interface ValidateForActivationResponse {
  valid: boolean;
  errors?: ActivationError[];
}

// -- Linked products --

export interface LinkedProduct {
  name: string;
  item_name: string;
  item_code: string;
}

export interface LinkedProductsResponse {
  products: LinkedProduct[];
}

// -- Rule builder constants --

export const RULE_OPERATORS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "is_set", label: "is set" },
  { value: "is_empty", label: "is empty" },
  { value: "contains", label: "contains" },
] as const;

export type RuleOperator = (typeof RULE_OPERATORS)[number]["value"];

export const RULE_ACTIONS = [
  { value: "Show Field", label: "show" },
  { value: "Hide Field", label: "hide" },
  { value: "Set Value", label: "set value of" },
  { value: "Force Value", label: "force value of" },
  { value: "Set Required", label: "make required" },
  { value: "Clear Required", label: "make optional" },
] as const;

export type RuleAction = (typeof RULE_ACTIONS)[number]["value"];

/** Operators that don't need a "Has Value" input */
export const VALUE_LESS_OPERATORS: RuleOperator[] = ["is_set", "is_empty"];

/** Actions that need a "Target Value" input */
export const VALUE_ACTIONS: RuleAction[] = ["Set Value", "Force Value"];

// -- Bundle types (Epic 04) --

export interface BundleChildItem {
  row_name: string;
  item_code: string;
  item_name: string;
  qty: number;
  image: string | null;
  has_variants?: boolean;
  default_variant?: string | null;
  template_name: string | null;
  template_display_name: string | null;
  template_status: "Draft" | "Active" | null;
  fields: Array<{
    label: string;
    field_type: FieldType;
    required: 0 | 1;
    shared: boolean;
  }>;
  shared_customization_fields: string[];
}

export interface BundleCustomizationConfig {
  is_bundle: boolean;
  bundle_name?: string;
  allow_variant_selection?: boolean;
  variant_pricing?: boolean;
  surcharge_mode?: "Max" | "Sum";
  linked_attributes?: string[];
  common_attributes?: string[];
  items?: BundleChildItem[];
}

export interface VariantOption {
  item_code: string;
  label: string;
}

// -- Field type enum --

export type FieldType =
  | "Text Input"
  | "Text Area"
  | "Single Select"
  | "Multi Select"
  | "Toggle"
  | "File Upload";

export const FIELD_TYPES: FieldType[] = [
  "Text Input",
  "Text Area",
  "Single Select",
  "Multi Select",
  "Toggle",
  "File Upload",
];
